import {
  Box,
  Icon,
  Tab,
  TabContext,
  TabList,
  TabPanel,
  Theme,
  Typography,
} from "@outschool/backpack";
import {
  ActivityRatingAndReviewsQuery as ActivityRatingAndReviewsQueryType,
  ActivityRatingAndReviewsQueryVariables,
  ClassDetailsQuery_ActivityFragment,
  PublicReviewFragmentFragment,
  PublicReviewsOfLeaderQuery as PublicReviewsOfLeaderQueryType,
  PublicReviewsOfLeaderQueryVariables,
} from "@outschool/gql-frontend-generated";
import { faSparkles } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import { pxToRem } from "@outschool/ui-utils";
import React, { useEffect, useMemo, useState } from "react";

import {
  ActivityRatingAndReviewsQuery,
  publicReviewsOfLeaderQuery,
} from "../index";
import ParentSubmittedPhotos from "../Reviews/ParentSubmittedPhotos";
import Reviews from "../Reviews/Reviews";

type LeaderReviewDataType = {
  recentReviews: PublicReviewFragmentFragment[];
  reviewsWithTextCount: number;
  averageActivityStarRating: number | null;
  reviewCount: number | null;
};

export enum TabValue {
  Class = "class",
  Leader = "leader",
}

function ReviewsTabLabel({
  textLabel,
  reviewCount,
}: {
  textLabel: string;
  reviewCount: number | null;
}) {
  return (
    <Box flex>
      <Typography
        sx={(theme: Theme) => ({
          fontSize: pxToRem(22),
          color: "grey.900",
          typography: "h5",
          marginRight: pxToRem(5),
          [theme.breakpoints.down("md")]: {
            fontSize: pxToRem(16),
            marginRight: pxToRem(2),
            typography: "subTitle1",
          },
        })}
      >
        {textLabel}
      </Typography>
      <Typography
        sx={(theme: Theme) => ({
          fontSize: pxToRem(22),
          typography: "h5",
          color: "grey.500",
          [theme.breakpoints.down("md")]: {
            fontSize: pxToRem(16),
            typography: "subTitle1",
          },
        })}
      >
        ({reviewCount})
      </Typography>
    </Box>
  );
}

export default function PublicReviewsOfActivity({
  activity,
  ReviewRespondButton,
  tabValue,
  setTabValue,
}: {
  activity: ClassDetailsQuery_ActivityFragment;
  ReviewRespondButton?: React.ElementType;
  tabValue: TabValue;
  setTabValue: (tabValue: TabValue) => void;
}) {
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\PublicReviewsOfActivity"
  );

  const [leaderReviewData, setLeaderReviewData] =
    useState<LeaderReviewDataType>({
      recentReviews: [],
      reviewsWithTextCount: 0,
      averageActivityStarRating: 0.0,
      reviewCount: 0,
    });

  const {
    data: classData,
    loading: classDataLoading,
    fetchMore: classDataFetchMore,
  } = useQueryWithPreviousData<
    ActivityRatingAndReviewsQueryType,
    ActivityRatingAndReviewsQueryVariables
  >(ActivityRatingAndReviewsQuery, {
    skip: !activity || !activity.uid,
    variables: {
      uid: activity.uid,
      offset: 0,
      limit: 6,
      skipAggregates: false,
      summaryVariant: "v2",
    },
  });

  const activityWithReviews =
    !classDataLoading && classData
      ? classData.activity
      : ({} as ActivityRatingAndReviewsQueryType["activity"]);

  const {
    reviewCount: classReviewCount = 0,
    reviewsWithTextCount: classReviewsWithTextCount = 0,
    reviewsWithAttachments: classReviewsWithAttachments = [],
    feedbackSummary: classFeedbackSummary = null,
  } = activityWithReviews;

  const classRecentReviews: ActivityRatingAndReviewsQueryType["activity"]["recentReviews"] =
    useMemo(() => {
      return activityWithReviews?.recentReviews || [];
    }, [activityWithReviews.recentReviews]);

  const {
    data: leaderData,
    loading: leaderDataLoading,
    fetchMore: leaderFetchMore,
  } = useQueryWithPreviousData<
    PublicReviewsOfLeaderQueryType,
    PublicReviewsOfLeaderQueryVariables
  >(publicReviewsOfLeaderQuery, {
    skip: !activity.leader.uid,
    variables: {
      leaderUid: activity.leader.uid,
      offset: 0,
      limit: 6,
      skipAggregates: false,
      includeSummary: true,
    },
  });

  const leaderDataLeader = leaderData?.publicProfile?.leader;
  const leaderFeedbackSummary = leaderDataLeader?.feedbackSummary;
  const isClassReviewsTab = classRecentReviews.length > 0;
  const isLeaderReviewsTab = leaderReviewData.recentReviews.length > 0;

  useEffect(() => {
    if (isClassReviewsTab) {
      setTabValue(TabValue.Class);
    } else if (isLeaderReviewsTab) {
      setTabValue(TabValue.Leader);
    }
  }, [isClassReviewsTab, isLeaderReviewsTab, setTabValue]);

  useEffect(() => {
    if (!leaderDataLoading && leaderDataLeader) {
      setLeaderReviewData({
        recentReviews: leaderDataLeader.recentReviews,
        reviewsWithTextCount: leaderDataLeader.reviewsWithTextCount || 0,
        averageActivityStarRating:
          leaderDataLeader.averageActivityStarRating || 0,
        reviewCount: leaderDataLeader.reviewCount,
      });
    }
  }, [leaderDataLeader, leaderDataLoading]);

  const handleShowMoreClick = React.useCallback(async () => {
    if (!classDataFetchMore || !classRecentReviews) {
      return;
    }
    const reviews = classRecentReviews || [];
    try {
      await classDataFetchMore({
        variables: {
          offset: reviews.length,
          limit: 6,
          skipAggregates: true,
        },
        updateQuery: (
          previousResult: ActivityRatingAndReviewsQueryType,
          { fetchMoreResult }
        ) => {
          if (!fetchMoreResult) {
            return previousResult;
          }
          return Object.assign({}, previousResult, {
            activity: {
              ...previousResult.activity,
              recentReviews: [
                ...previousResult.activity.recentReviews,
                ...fetchMoreResult.activity.recentReviews,
              ],
            },
          });
        },
      });
    } catch (e) {
      console.log("Warning: could not fetch more:", e);
    }
  }, [classDataFetchMore, classRecentReviews]);

  const handleLeaderReviewShowMoreClick = React.useCallback(async () => {
    if (!leaderFetchMore || !leaderReviewData.recentReviews) {
      return;
    }
    const reviews = leaderReviewData.recentReviews || [];
    try {
      await leaderFetchMore({
        variables: {
          offset: reviews.length,
          limit: 6,
          skipAggregates: true,
          includeSummary: false,
        },
        updateQuery: (
          leaderReviewPreviousResult: PublicReviewsOfLeaderQueryType,
          { fetchMoreResult: leaderFetchMore }
        ) => {
          if (!leaderFetchMore) {
            return leaderReviewPreviousResult;
          }

          const updatedLeader = Object.assign({}, leaderReviewPreviousResult, {
            publicProfile: {
              ...leaderReviewPreviousResult?.publicProfile,
              leader: {
                ...leaderReviewPreviousResult?.publicProfile?.leader,
                recentReviews: [
                  ...(leaderReviewPreviousResult?.publicProfile?.leader
                    ?.recentReviews ?? []),
                  ...(leaderFetchMore?.publicProfile?.leader?.recentReviews ??
                    []),
                ],
              },
            },
          });

          setLeaderReviewData({
            recentReviews:
              updatedLeader?.publicProfile?.leader?.recentReviews ?? [],
            reviewsWithTextCount: leaderReviewData.reviewsWithTextCount,
            averageActivityStarRating:
              leaderReviewData.averageActivityStarRating,
            reviewCount: leaderReviewData.reviewCount,
          });

          return updatedLeader;
        },
      });
    } catch (e) {
      console.log("Warning: could not fetch more:", e);
    }
  }, [leaderFetchMore, leaderReviewData]);

  if (
    classRecentReviews.length === 0 &&
    leaderReviewData.recentReviews.length === 0
  ) {
    return null;
  }

  const handleChange = (event: React.SyntheticEvent, tabValue: TabValue) => {
    setTabValue(tabValue);
  };

  return (
    <Box sx={{ width: "100%", typography: "body1" }}>
      <TabContext value={tabValue || TabValue.Class}>
        <TabList
          onChange={handleChange}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            ".Mui-selected": { color: "black" },
            "& .MuiTabs-indicator": { backgroundColor: "black" },
          }}
        >
          {isClassReviewsTab && (
            <Tab
              label={
                <ReviewsTabLabel
                  textLabel={t("For this class")}
                  reviewCount={classReviewCount}
                />
              }
              value={TabValue.Class}
              style={{
                paddingLeft: 0,
              }}
            />
          )}
          {isLeaderReviewsTab && (
            <Tab
              label={
                <ReviewsTabLabel
                  textLabel={t("For this teacher")}
                  reviewCount={leaderDataLeader?.reviewCount ?? null}
                />
              }
              value={TabValue.Leader}
              style={{
                paddingLeft: isClassReviewsTab ? pxToRem(12) : 0,
              }}
            />
          )}
        </TabList>
        {isClassReviewsTab && (
          <TabPanel
            value={TabValue.Class}
            sx={(theme: Theme) => ({
              paddingTop: 24,
              [theme.breakpoints.down("md")]: {
                paddingTop: undefined,
              },
            })}
          >
            <>
              <Typography
                sx={{ mb: 12, color: "grey.600" }}
                as="p"
                variant="body1"
              >
                {t(
                  "{{reviewCount}} total ratings, {{reviewsWithTextCount}} with reviews",
                  {
                    reviewCount: classReviewCount,
                    reviewsWithTextCount: classReviewsWithTextCount,
                  }
                )}
              </Typography>

              {classFeedbackSummary?.pros && (
                <FeedbackSummary
                  summary={classFeedbackSummary.pros}
                  title={t`Review summary for this class`}
                />
              )}

              {classReviewsWithAttachments.length > 0 ? (
                <ParentSubmittedPhotos
                  reviewsWithAttachments={classReviewsWithAttachments}
                />
              ) : null}
              <Reviews
                reviews={classRecentReviews}
                leader={activity.leader}
                handleShowMoreClick={handleShowMoreClick}
                ReviewRespondButton={ReviewRespondButton}
                reviewsWithTextCount={classReviewsWithTextCount}
                recentReviewsCount={classRecentReviews.length}
              />
            </>
          </TabPanel>
        )}
        {isLeaderReviewsTab && (
          <TabPanel
            value={TabValue.Leader}
            sx={(theme: Theme) => ({
              marginTop: pxToRem(24),
              [theme.breakpoints.down("md")]: {
                marginTop: undefined,
              },
            })}
          >
            <Typography
              sx={{ mb: 12, color: "grey.600" }}
              as="p"
              variant="body1"
            >
              {t(
                "{{reviewCount}} total ratings, {{reviewsWithTextCount}} with reviews",
                {
                  reviewCount: leaderReviewData.reviewCount,
                  reviewsWithTextCount: leaderReviewData.reviewsWithTextCount,
                }
              )}
            </Typography>

            {leaderFeedbackSummary?.pros && (
              <FeedbackSummary
                summary={leaderFeedbackSummary.pros}
                title={t`Review summary for this teacher`}
              />
            )}

            <Reviews
              reviews={leaderReviewData.recentReviews}
              leader={activity.leader}
              handleShowMoreClick={handleLeaderReviewShowMoreClick}
              ReviewRespondButton={ReviewRespondButton}
              reviewsWithTextCount={leaderReviewData?.reviewsWithTextCount}
              recentReviewsCount={leaderReviewData?.recentReviews?.length}
              isLeaderReviewsTab
            />
          </TabPanel>
        )}
      </TabContext>
    </Box>
  );
}

function FeedbackSummary({
  summary,
  title,
}: {
  summary: string;
  title: string;
}) {
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\PublicReviewsOfActivity"
  );

  return (
    <>
      <Box
        sx={(theme: Theme) => ({
          marginTop: "1.25em",
          marginBottom: "0.5em",
          padding: "1em",
          borderRadius: "0.5em",
          backgroundColor: "primary.50",
          // align with review grid on xl size
          [theme.breakpoints.up("xl")]: {
            marginRight: "9px",
          },
        })}
      >
        <Typography as="div" variant="h6" sx={{ marginBottom: "0.5em" }}>
          <Icon icon={faSparkles} sx={{ marginRight: "0.5em" }} />
          {title}
        </Typography>
        <Typography as="div" variant="body1" sx={{ marginBottom: "0.5em" }}>
          {summary}
        </Typography>
        <Typography
          as="div"
          variant="caption"
        >{t`This summary was AI-generated from parent reviews`}</Typography>
      </Box>
    </>
  );
}
